import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'

import Button from 'lib/components/Button/Button'
import CheckboxInput from 'lib/components/Form/CheckboxInput/CheckboxInput'
import Icon from 'lib/components/Icon/Icon'
import Modal, { useModal } from 'lib/components/Modal/Modal'

import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'
import { Order } from '@/types/order'
import { getBaseUrl } from '@/utils/urlHelpers'

export const PRINT_LABELS_MODAL_ID = 'print-labels-modal'

interface PrintLabelsProps {
  order: Order
  legType?: 'outbound' | 'return' | 'all'
  afterHide?: () => void
  isAdmin?: boolean
}

export const PrintLabelsModal: React.FC<PrintLabelsProps> = ({
  order,
  legType = 'all',
  afterHide,
  isAdmin = false,
}: PrintLabelsProps) => {
  const { control, handleSubmit, setValue, watch } = useForm()
  const { track } = useRudderstackAnalytics()
  const dialogArgs = useModal((state) => state.dialogArgs[PRINT_LABELS_MODAL_ID])
  const [trackedIds, setTrackingIds] = useState<string[]>([])
  const watchedTrackingIds = watch('tracking_ids', [])
  const didInitRef = useRef(false)

  useEffect(() => {
    // Always sync the form value to local trackedIds, including when the
    // array becomes empty. This ensures unchecking all boxes clears state.
    setTrackingIds(watchedTrackingIds || [])
  }, [watchedTrackingIds])

  useEffect(() => {
    const args = dialogArgs

    // Collect all valid tracking IDs from filtered legs
    const filteredLegs =
      order?.legs?.filter((leg) => {
        const currentLegType = args?.legType ?? legType
        if (currentLegType === 'all') return true
        const leg_name = leg.leg_index == 1 ? 'outbound' : 'return'
        return leg_name === currentLegType
      }) || []

    const allValidTrackingIds: string[] = []
    filteredLegs.forEach((leg) => {
      leg?.travelers?.forEach((traveler) => {
        traveler.shipments
          .filter((s) => s.state !== 'cancelled' && s.products.some((p) => p.type === 'label'))
          .forEach((s) => {
            allValidTrackingIds.push(s.tracking_id)
          })
      })
    })

    // Use trackingIds from dialog args if provided.
    // If no args are provided, only initialize to allValidTrackingIds when we
    // don't already have tracked IDs (preserve previous selections across opens).
    const initialIds = args?.trackingIds ?? allValidTrackingIds

    // If the caller provided explicit dialog args, always honor them and
    // re-initialize (modal-open driven). Otherwise initialize only once
    // on first mount/open — don't re-init when trackedIds becomes empty
    // due to user interaction (that would force all boxes back checked).
    if (args?.trackingIds) {
      setTrackingIds(initialIds)
      setValue('tracking_ids', initialIds)
      didInitRef.current = true
      return
    }

    if (!didInitRef.current) {
      setTrackingIds(initialIds)
      setValue('tracking_ids', initialIds)
      didInitRef.current = true
    }
  }, [order, legType, dialogArgs, setValue, trackedIds.length])

  const handleAfterHide = useCallback(() => {
    // Preserve trackedIds so selections persist if the modal is reopened.
    // If caller wants it cleared, they can pass args next time or use the
    // optional afterHide callback to reset externally.
    if (afterHide) {
      afterHide()
    }
  }, [afterHide])

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (formData) => {
      track?.('order_confirmation_print_labels', { order_id: order.id })

      const queryParams = new URLSearchParams()

      for (const [key, value] of Object.entries(formData)) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            queryParams.append(`${key}[]`, v)
          })
        } else if (value) {
          queryParams.append(key, value as string)
        }
      }

      const queryString = queryParams.toString()
      const url = `${getBaseUrl()}/orders/${order.id}/labels/print_all?${queryString}`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    [order.id, track],
  )

  const toggleTravelerShipments = useCallback(
    (checked: boolean, shipmentIds: string[]) => {
      const current = (trackedIds || []).map((i) => String(i))
      const ids = shipmentIds.map((i) => String(i))

      const next: string[] = checked
        ? Array.from(new Set([...current, ...ids]))
        : current.filter((id: string) => !ids.includes(String(id)))

      setValue('tracking_ids', next, { shouldDirty: true })
    },
    [trackedIds, setValue],
  )

  const updateTrackingIds = useCallback(
    (checked: boolean, shipmentId: string) => {
      const id = String(shipmentId)
      const current = (trackedIds || []).map((i) => String(i))

      const next = checked
        ? Array.from(new Set([...current, id]))
        : current.filter((i: string) => i !== id)

      setValue('tracking_ids', next, { shouldDirty: true })
      // Intentionally no debug logging here to satisfy lint rules
    },
    [trackedIds, setValue],
  )

  // Filter legs based on legType
  const filteredLegs =
    order?.legs?.filter((leg) => {
      const currentLegType = dialogArgs?.legType ?? legType
      if (currentLegType === 'all') return true

      const leg_name = leg.leg_index == 1 ? 'outbound' : 'return'
      return leg_name === currentLegType
    }) || []

  const formRef = useRef<HTMLFormElement>(null)

  return (
    <>
      <Modal
        id={PRINT_LABELS_MODAL_ID}
        title="Print shipping labels"
        afterHide={handleAfterHide}
        content={
          <>
            <div className="mb-7 text-center text-sm">
              Select the shipping labels you would like to print.
            </div>
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="flex w-[400px] max-w-full flex-col gap-y-4 px-2">
                {filteredLegs.map((leg) => {
                  const leg_name = leg.leg_index == 1 ? 'outbound' : 'return'
                  const origin = `${leg?.origin?.city}, ${leg?.origin?.state}`
                  const destination = `${leg?.destination?.city}, ${leg?.destination?.state}`

                  return (
                    <div
                      className="rounded-3xl border border-neutral-500 px-6 py-4"
                      key={leg.leg_index}
                    >
                      {/* Header */}
                      <div className="mb-2 text-sm font-semibold">
                        {leg_name.charAt(0).toUpperCase() + leg_name.slice(1)}
                      </div>
                      <div className="mb-4 flex items-center justify-start">
                        <p className="type-body-8">{origin}</p>
                        <Icon icon="arrow-right" className="mx-2 size-6 text-theme-accessibility" />
                        <p className="type-body-8">{destination}</p>
                      </div>
                      {leg?.travelers?.map((traveler) => {
                        const validShipmentIds = traveler.shipments
                          .filter(
                            (s) =>
                              s.state !== 'cancelled' && s.products.some((p) => p.type === 'label'),
                          )
                          .map((s) => s.tracking_id)

                        const travelerChecked = validShipmentIds.every((id) =>
                          trackedIds.includes(id),
                        )
                        return (
                          <Fragment key={traveler.id}>
                            <div className="mb-4">
                              <CheckboxInput
                                name={`traveler-${traveler.id}`}
                                control={control}
                                label={traveler.name}
                                checked={travelerChecked}
                                onChange={(checked) =>
                                  toggleTravelerShipments(checked, validShipmentIds)
                                }
                              />
                            </div>
                            {traveler?.shipments?.map((shipment) => {
                              const label = shipment.products.filter(
                                (product) => product.type == 'label',
                              )[0]
                              if (!label) return null

                              const labelName = `${label?.full_name} - ${shipment.tracking_id}`

                              if (shipment.state == 'cancelled') {
                                if (!isAdmin) return null
                                return (
                                  <div className="mb-5 ml-6" key={shipment.id}>
                                    <div className="mt-4 flex items-center gap-2">
                                      <Icon
                                        icon="error-circle-filled"
                                        className="size-6 text-red-500"
                                      />
                                      <p className="mb-1 text-red-500">
                                        Your bag has been cancelled
                                      </p>
                                    </div>
                                  </div>
                                )
                              }
                              return (
                                <div className="mb-5 ml-6" key={shipment.id}>
                                  <CheckboxInput
                                    name="tracking_ids[]"
                                    control={control}
                                    label={labelName}
                                    checked={trackedIds.includes(shipment.tracking_id)}
                                    onChange={(checked) =>
                                      updateTrackingIds(checked, shipment.tracking_id)
                                    }
                                  />
                                </div>
                              )
                            })}
                          </Fragment>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
              <Button type="submit" className="mt-4 w-full" disabled={trackedIds.length === 0}>
                Print
              </Button>
            </form>
          </>
        }
      />
    </>
  )
}
